import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { Outlet } from '@/lib/router-components';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export const Route = createFileRoute('/client/processes/$processId')({
  component: ClientProcessLayout,
});

function ClientProcessLayout() {
  const { processId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: processHeader, isLoading, error } = useQuery<any>({
    queryKey: ['client-portal', 'process', processId],
    queryFn: async () => {
      const r = await api.get(`/client-portal/processes/${processId}`);
      return r.data?.data ?? r.data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const headerTitle =
    processHeader?.internalTitle || processHeader?.parties || 'Processo';
  const headerSubtitle = processHeader?.processNumber ?? processId;

  const isTasksTab = location.pathname.endsWith('/tasks');
  const isSyncHistoryTab = location.pathname.endsWith('/sync-history');
  const isIndexTab = !isTasksTab && !isSyncHistoryTab;

  if (error) {
    return (
      <div className="p-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div className="max-w-7xl mx-auto bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
          <p className="text-sm text-red-600">
            Não foi possível carregar o processo. Verifique se você tem acesso a ele.
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/client' })}
            className="mt-4 text-sm text-[#06141B] underline"
          >
            Voltar para início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              {isLoading ? (
                <div className="h-5 w-48 bg-gray-100 rounded animate-pulse" />
              ) : (
                <h1 className="text-[16px] font-semibold text-[#06141B] truncate">{headerTitle}</h1>
              )}
              <p className="text-[12px] text-gray-500 mt-1 truncate">{headerSubtitle}</p>

              {!isLoading && Array.isArray(processHeader?.parts) && processHeader.parts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                  {processHeader.parts.map((part: any) => (
                    <span key={part.id} className="text-[11px] text-gray-500">
                      {part.name}
                      {part.pole ? (
                        <span className="text-[10px] text-gray-400 ml-1">({part.pole})</span>
                      ) : null}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}>
            <nav className="flex space-x-4">
              <button
                onClick={() => navigate({ to: `/client/processes/${processId}/` })}
                className={`${isIndexTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  color: isIndexTab ? '#06141B' : '#9BA8AB',
                  borderBottomColor: isIndexTab ? '#06141B' : 'transparent',
                }}
              >
                Informações
              </button>
              <button
                onClick={() => navigate({ to: `/client/processes/${processId}/tasks` })}
                className={`${isTasksTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  color: isTasksTab ? '#06141B' : '#9BA8AB',
                  borderBottomColor: isTasksTab ? '#06141B' : 'transparent',
                }}
              >
                Tarefas
              </button>
              <button
                onClick={() => navigate({ to: `/client/processes/${processId}/sync-history` })}
                className={`${isSyncHistoryTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  color: isSyncHistoryTab ? '#06141B' : '#9BA8AB',
                  borderBottomColor: isSyncHistoryTab ? '#06141B' : 'transparent',
                }}
              >
                Histórico
              </button>
            </nav>
          </div>
        </div>

        <div className="mt-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
